import {
  hasVisibleTimelineSummary,
  type TimelineEventView,
  type TimelineGraphLayout,
  type TimelineGraphMarkerView,
  type TimelineGraphMilestoneView,
} from "@/features/timeline/timeline.types";
import type { SubscriptionTier } from "@/types/projects";

export interface TimelineGraphLayoutInput {
  events: TimelineEventView[];
  selectedEventId: string | null;
  tier: SubscriptionTier;
  viewportWidth: number;
}

const GRAPH_HEIGHT = 920;
const AXIS_Y = 440;
const GRAPH_PADDING_X = 88;
const CARD_WIDTH = 272;
const CARD_MIN_HEIGHT = 156;
const STEM_LEVELS = [126, 214, 302];
const BRANCH_COLORS = [
  "#8b5cf6",
  "#10b981",
  "#ec4899",
  "#f59e0b",
  "#06b6d4",
  "#ef4444",
  "#3b82f6",
  "#84cc16",
];

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function estimateCardHeight(summaryLine: string | null, hasLinkLabel: boolean) {
  const contentLength = summaryLine?.trim().length ?? 0;
  const estimatedLines = contentLength > 0 ? Math.min(Math.ceil(contentLength / 46), 4) : 2;
  const linkHeight = hasLinkLabel ? 32 : 0;

  return Math.max(CARD_MIN_HEIGHT, 114 + estimatedLines * 22 + linkHeight);
}

function extractMilestoneLabel(event: TimelineEventView) {
  const label = event.eraLabel.trim();
  const yearMatch = label.match(/\b(\d{4})\b/);

  return yearMatch?.[1] ?? (label || "Undated");
}

type TimelineMilestoneGroup = {
  events: TimelineEventView[];
  key: string;
  label: string;
};

function createMilestoneGroups(events: TimelineEventView[]) {
  const groups: TimelineMilestoneGroup[] = [];
  const groupIndexByKey = new Map<string, number>();

  events.forEach((event) => {
    const label = extractMilestoneLabel(event);
    const key = label.toLowerCase();
    const existingIndex = groupIndexByKey.get(key);

    if (existingIndex != null) {
      groups[existingIndex]?.events.push(event);
      return;
    }

    groupIndexByKey.set(key, groups.length);
    groups.push({
      events: [event],
      key,
      label,
    });
  });

  return groups;
}

function createMilestones(groups: TimelineMilestoneGroup[], positions: number[]) {
  return groups.map<TimelineGraphMilestoneView>((group, index) => ({
    label: group.label,
    x: positions[index] ?? GRAPH_PADDING_X,
  }));
}

function createBranchPath({
  axisY,
  branchSide,
  cardX,
  cardY,
  cardHeight,
  cardWidth,
  direction,
  trunkX,
}: {
  axisY: number;
  branchSide: "left" | "right";
  cardX: number;
  cardY: number;
  cardHeight: number;
  cardWidth: number;
  direction: "up" | "down";
  trunkX: number;
}) {
  const verticalTargetY = direction === "up" ? cardY + cardHeight : cardY;
  const connectorX = branchSide === "right" ? cardX : cardX + cardWidth;
  const curveY = direction === "up" ? verticalTargetY + 22 : verticalTargetY - 22;
  const controlOffsetX = branchSide === "right" ? 26 : -26;

  return [
    `M ${trunkX} ${axisY}`,
    `C ${trunkX} ${(axisY + curveY) / 2}, ${trunkX + controlOffsetX} ${curveY}, ${trunkX + controlOffsetX * 1.35} ${curveY}`,
    `S ${connectorX - controlOffsetX * 0.8} ${verticalTargetY}, ${connectorX} ${verticalTargetY}`,
  ].join(" ");
}

function createMarkers(
  events: TimelineEventView[],
  groups: TimelineMilestoneGroup[],
  positions: number[],
  tier: SubscriptionTier,
  selectedEventId: string | null,
  width: number,
) {
  const groupMeta = new Map(groups.map((group, groupIndex) => [
    group.key,
    {
      groupIndex,
      label: group.label,
      nextLocalIndex: 0,
      trunkX: positions[groupIndex] ?? GRAPH_PADDING_X,
    },
  ]));

  return events.map<TimelineGraphMarkerView>((event, index) => {
    const milestoneLabel = extractMilestoneLabel(event);
    const group = groupMeta.get(milestoneLabel.toLowerCase());
    const groupIndex = group?.groupIndex ?? 0;
    const localIndex = group?.nextLocalIndex ?? 0;

    if (group) {
      group.nextLocalIndex += 1;
    }

    const direction = (groupIndex + localIndex) % 2 === 0 ? "up" : "down";
    const branchTier = Math.floor(localIndex / 2);
    const level = STEM_LEVELS[(groupIndex + branchTier) % STEM_LEVELS.length];
    const branchSide =
      localIndex % 2 === 0
        ? groupIndex % 2 === 0
          ? "right"
          : "left"
        : groupIndex % 2 === 0
          ? "left"
          : "right";
    const trunkX = group?.trunkX ?? GRAPH_PADDING_X;
    const cardHeight = estimateCardHeight(
      hasVisibleTimelineSummary(tier, event) ? event.aiSummary : event.descriptionPreview,
      Boolean(event.links[0]),
    );
    const y = direction === "up" ? AXIS_Y - level : AXIS_Y + level;
    const cardY = direction === "up" ? y - cardHeight : y;
    const branchOffset = clamp(84 + branchTier * 62 + (groupIndex % 2) * 12, 84, 192);
    const cardX = clamp(
      branchSide === "right"
        ? trunkX + branchOffset
        : trunkX - CARD_WIDTH - branchOffset,
      GRAPH_PADDING_X,
      Math.max(GRAPH_PADDING_X, width - GRAPH_PADDING_X - CARD_WIDTH),
    );
    const summaryLine = hasVisibleTimelineSummary(tier, event)
      ? event.aiSummary
      : event.descriptionPreview;
    const branchColor = BRANCH_COLORS[index % BRANCH_COLORS.length];

    return {
      branchColor,
      branchPathD: createBranchPath({
        axisY: AXIS_Y,
        branchSide,
        cardHeight,
        cardWidth: CARD_WIDTH,
        cardX,
        cardY,
        direction,
        trunkX,
      }),
      branchSide,
      cardHeight,
      cardWidth: CARD_WIDTH,
      cardX,
      cardY,
      direction,
      eraLabel: event.eraLabel,
      eventId: event.id,
      index,
      isSelected: event.id === selectedEventId,
      linkLabel: event.links[0]?.label ?? null,
      markerShape: summaryLine ? "rect" : "circle",
      milestoneLabel,
      summaryLine,
      title: event.title,
      trunkX,
      x: trunkX,
      y,
    };
  });
}

export function buildTimelineGraphLayout({
  events,
  selectedEventId,
  tier,
  viewportWidth,
}: TimelineGraphLayoutInput): TimelineGraphLayout {
  const groups = createMilestoneGroups(events);
  const widestGroup = groups.reduce((max, group) => Math.max(max, group.events.length), 1);
  const width = Math.max(viewportWidth, 1180, groups.length * 320 + widestGroup * 128);
  const usableWidth = Math.max(width - GRAPH_PADDING_X * 2, 1);
  const spacing = groups.length > 1 ? usableWidth / (groups.length - 1) : 0;
  const positions = groups.map((_, index) =>
    groups.length === 1 ? width / 2 : GRAPH_PADDING_X + spacing * index,
  );
  const markers = createMarkers(events, groups, positions, tier, selectedEventId, width);

  return {
    axisY: AXIS_Y,
    height: GRAPH_HEIGHT,
    markers,
    milestones: createMilestones(groups, positions),
    pathKind: "ramified",
    width,
  };
}
